import {Component, computed, input} from '@angular/core';
import {CurrencyPipe} from '@angular/common';
import {EventRevenue} from "../../_services/event-helper";

/**
 * The „Umsatz" tile of the event-details page (#817): what the event sold, and how that figure is reached.
 *
 * The page used to show „Artikel" (the line prices, 99,70 € on closing #194's evening) next to „Gesamtbetrag"
 * (76,93 €, the money taken) and said nothing about the 22,77 € between them — an order-level Rabatt and
 * Aufschlag, and 21,20 € sold on account. The tile now states the revenue the two cash-point reports print,
 * with the rows that get a reader from the articles to it and from it to what was actually paid.
 *
 * The detail rows print only when they say something: a Rabatt or Aufschlag row at 0,00 € is noise on the
 * evenings nobody gave a discount, and the „davon" split repeats the total when nothing was sold on account.
 * That is the rule the Z-Bericht's Umsatz block already follows.
 */
/**
 * Half a cent: below this, two amounts are the same money. `total` is summed client-side
 * (`EventHelper.getRevenue`: `articles + discount + surcharge + other`) while `paid` arrives already summed
 * server-side, in a different association order — so the two can land a `Number.EPSILON`-scale bit apart
 * even when every underlying figure agrees to the cent (e.g. articles 2,79 €, discount −40,46 €, discount
 * −83,03 €, surcharge 29,15 € gives `total = −91.55000000000001` against a server `paid` of exactly
 * `−91.55`). An exact `!==` read that as a real discrepancy and showed the „davon" split on a closing
 * that had none.
 */
const MONEY_EPSILON = 0.005;

/** Whether two money amounts differ by at least half a cent — see {@link MONEY_EPSILON}. */
function differsByMoney(a: number, b: number): boolean {
  return Math.abs(a - b) >= MONEY_EPSILON;
}

@Component({
  selector: 'app-event-revenue-tile',
  templateUrl: './event-revenue-tile.component.html',
  imports: [CurrencyPipe],
  host: {'class': 'block rounded-lg bg-white px-4 py-3 shadow-lg sm:px-6 lg:px-8'}
})
export class EventRevenueTileComponent {
  /** The event's revenue, as the server aggregated it over the whole event window. */
  readonly revenue = input.required<EventRevenue>();

  /** Whether an order-level discount or surcharge changed the articles' total at all. */
  protected readonly hasAdjustments = computed(() =>
    differsByMoney(this.revenue().discount, 0) || differsByMoney(this.revenue().surcharge, 0));

  /**
   * Whether the event booked revenue that is neither an article line nor an order-level Rabatt/Aufschlag —
   * a deposit handed back, a single-purpose voucher sold or redeemed, an advance received or cleared.
   *
   * Its own row rather than part of {@link hasAdjustments}: an evening that gave a discount and sold no
   * voucher would otherwise print a 0,00 € line, and the rule on this tile is that a row appears when it says
   * something. A sold deposit never opens this row — its article category makes it a line, so it is already
   * in „Artikel und Sonstiges".
   */
  protected readonly hasOther = computed(() => differsByMoney(this.revenue().other, 0));

  /**
   * Whether the split into paid and owed says more than the total does. It does as soon as anything was sold
   * on account, and also when the two halves do NOT add up — which is a data inconsistency the tile must
   * show rather than hide behind a rounded total.
   */
  protected readonly hasReceivables = computed(() =>
    differsByMoney(this.revenue().receivables, 0) || differsByMoney(this.revenue().paid, this.revenue().total));
}
